import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Table, Tag, Button, Empty, Alert, Tooltip, message } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import {
  FiDownload,
  FiRefreshCw,
  FiCopy,
  FiExternalLink,
} from 'react-icons/fi';
import axios from 'axios';
import { ENCUESTAS } from './data';

import '../../../assets/css/publicaciones.css';

const API_BASE = 'https://api-sitio.canimolt.org';

// CSV-safe encoding: wrap any value in quotes, escape internal quotes.
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/"/g, '""');
  return `"${s}"`;
};

const downloadCsv = (rows, headers, filename) => {
  const head = headers.map((h) => csvCell(h.label)).join(',');
  const body = rows.map((row) => headers.map((h) => csvCell(h.value(row))).join(',')).join('\n');
  const csv = '﻿' + head + '\n' + body + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Color-code Sí / No / Pudiera ser tags so the table reads at a glance.
const answerColor = (val) => {
  const s = String(val || '').toLowerCase();
  if (s === 'sí' || s === 'si') return '#22c55e';   // green
  if (s === 'no') return '#ef4444';                  // red
  if (s === 'pudiera ser') return '#f59e0b';         // amber
  return '#9ca3af';                                  // gray
};

const formatDate = (s) => (s ? new Date(s).toLocaleString('es-MX', {
  dateStyle: 'medium',
  timeStyle: 'short',
}) : '');

const formatRelative = (s) => {
  if (!s) return '';
  const diffMs = Date.now() - new Date(s).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'hace unos segundos';
  const min = Math.round(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const days = Math.round(hr / 24);
  return `hace ${days} día${days === 1 ? '' : 's'}`;
};

const Card = ({ children, style }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #ececec',
      borderRadius: 8,
      padding: 20,
      ...style,
    }}
  >
    {children}
  </div>
);

const EncuestaAdmin = () => {
  const { identificador } = useParams();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';
  const encuestaConfig = ENCUESTAS[identificador];

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE}/api/encuestas/${encodeURIComponent(identificador)}`,
        { params: { token } }
      );
      setData(res.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Token inválido o ausente. Agrega ?token=… a la URL.');
      } else {
        setError('No se pudieron cargar las respuestas.');
      }
    }
  }, [identificador, token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchData().finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [fetchData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Tally per question, using the friendly labels from data.js.
  const tally = useMemo(() => {
    if (!data || !data.data || !encuestaConfig) return [];
    return encuestaConfig.preguntas.map((p) => {
      const counts = {};
      for (const row of data.data) {
        const v = row.respuestas && row.respuestas[p.id];
        const key = v == null ? '(sin respuesta)' : String(v);
        counts[key] = (counts[key] || 0) + 1;
      }
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      const orderedKeys = [
        ...p.opciones.filter((o) => o in counts),
        ...Object.keys(counts).filter((k) => !p.opciones.includes(k)),
      ];
      return { id: p.id, csvLabel: p.csvLabel, texto: p.texto, counts, total, orderedKeys };
    });
  }, [data, encuestaConfig]);

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Fecha',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        defaultSortOrder: 'descend',
        render: (v) => (
          <Tooltip title={formatDate(v)}>
            <span style={{ color: '#555', fontSize: 13 }}>{formatRelative(v)}</span>
          </Tooltip>
        ),
      },
    ];
    if (encuestaConfig) {
      for (const p of encuestaConfig.preguntas) {
        cols.push({
          title: p.csvLabel,
          key: p.id,
          render: (_, row) => {
            const ans = row.respuestas && row.respuestas[p.id];
            return ans
              ? <Tag style={{ background: answerColor(ans), color: '#fff', border: 'none', fontWeight: 500 }}>{ans}</Tag>
              : <span style={{ color: '#bbb' }}>—</span>;
          },
          filters: p.opciones.map((o) => ({ text: o, value: o })),
          onFilter: (val, row) => row.respuestas && row.respuestas[p.id] === val,
        });
      }
    } else {
      cols.push({
        title: 'Respuestas',
        dataIndex: 'respuestas',
        key: 'respuestas',
        render: (r) => r ? JSON.stringify(r) : '',
      });
    }
    cols.push(
      { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', render: (v) => v || <span style={{ color: '#bbb' }}>—</span> },
      { title: 'Apellido', dataIndex: 'apellido', key: 'apellido', render: (v) => v || <span style={{ color: '#bbb' }}>—</span> },
      { title: 'Empresa', dataIndex: 'empresa', key: 'empresa', render: (v) => v || <span style={{ color: '#bbb' }}>—</span> },
      { title: 'Correo', dataIndex: 'email', key: 'email', render: (v) => v || <span style={{ color: '#bbb' }}>—</span> },
      { title: 'Comentarios', dataIndex: 'comentarios', key: 'comentarios', render: (v) => v || <span style={{ color: '#bbb' }}>—</span> },
    );
    return cols;
  }, [encuestaConfig]);

  const handleDownloadCsv = () => {
    if (!data || !data.data) return;
    const headers = [{ label: 'Fecha', value: (r) => formatDate(r.created_at) }];
    if (encuestaConfig) {
      for (const p of encuestaConfig.preguntas) {
        headers.push({ label: p.csvLabel, value: (r) => (r.respuestas && r.respuestas[p.id]) || '' });
      }
    } else {
      headers.push({ label: 'Respuestas (JSON)', value: (r) => JSON.stringify(r.respuestas || {}) });
    }
    headers.push(
      { label: 'Nombre', value: (r) => r.nombre || '' },
      { label: 'Apellido', value: (r) => r.apellido || '' },
      { label: 'Empresa', value: (r) => r.empresa || '' },
      { label: 'Correo', value: (r) => r.email || '' },
      { label: 'Comentarios', value: (r) => r.comentarios || '' },
    );
    downloadCsv(data.data, headers, `encuesta-${identificador}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const publicUrl = `${window.location.origin}/encuestas/${identificador}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicUrl)}&margin=4`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Participa en la encuesta de CANIMOLT: ${publicUrl}`
  )}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      message.success('Enlace copiado');
    } catch {
      message.error('No se pudo copiar');
    }
  };

  const lastResponse = data && data.data && data.data.length > 0
    ? data.data.reduce((latest, r) => new Date(r.created_at) > new Date(latest.created_at) ? r : latest, data.data[0])
    : null;

  if (error) {
    return (
      <Row>
        <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 4 }} style={{ padding: '40px 0' }}>
          <Alert type="error" message={error} showIcon />
        </Col>
      </Row>
    );
  }

  return (
    <div style={{ background: '#f7f7f7', minHeight: '100vh', paddingBottom: 40 }}>
      {/* Hero */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '32px 0 24px' }}>
        <Row>
          <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, color: '#888', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Panel de respuestas · ID {identificador}
                </div>
                <h1 style={{ fontSize: 28, fontWeight: 700, margin: '4px 0 0', color: '#222' }}>
                  {encuestaConfig ? encuestaConfig.titulo : `Encuesta ${identificador}`}
                </h1>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  icon={<FiRefreshCw style={{ verticalAlign: 'middle' }} />}
                  onClick={onRefresh}
                  loading={refreshing}
                >
                  Actualizar
                </Button>
                <Button
                  type="primary"
                  icon={<FiDownload style={{ verticalAlign: 'middle' }} />}
                  onClick={handleDownloadCsv}
                  disabled={!data || !data.data || !data.data.length}
                >
                  Descargar Excel
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Row style={{ marginTop: 24 }} gutter={[16, 16]}>
        <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
          {/* Top: stats + share */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card>
                <div style={{ color: '#888', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total de respuestas
                </div>
                <div style={{ fontSize: 56, fontWeight: 700, color: '#222', lineHeight: 1, marginTop: 8 }}>
                  {data ? data.total : '—'}
                </div>
                {lastResponse && (
                  <div style={{ color: '#888', fontSize: 13, marginTop: 12 }}>
                    Última respuesta {formatRelative(lastResponse.created_at)}
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24} md={16}>
              <Card>
                <div style={{ color: '#888', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                  Compartir
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <img src={qrUrl} alt="QR" width={120} height={120} style={{ border: '1px solid #eee', borderRadius: 4 }} />
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ background: '#f5f5f5', padding: '10px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, color: '#444', wordBreak: 'break-all', marginBottom: 12 }}>
                      {publicUrl}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button icon={<FiCopy style={{ verticalAlign: 'middle' }} />} onClick={copyUrl}>Copiar enlace</Button>
                      <Button
                        icon={<FiExternalLink style={{ verticalAlign: 'middle' }} />}
                        href={whatsappUrl}
                        target="_blank"
                      >
                        Compartir por WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Tally per question */}
          {tally.length > 0 && (
            <Card style={{ marginTop: 16 }}>
              <div style={{ color: '#888', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                Resultados por pregunta
              </div>
              {tally.map((t, idx) => (
                <div
                  key={t.id}
                  style={{
                    paddingBottom: idx < tally.length - 1 ? 24 : 0,
                    marginBottom: idx < tally.length - 1 ? 24 : 0,
                    borderBottom: idx < tally.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 14, color: '#333', fontSize: 15, lineHeight: 1.4 }}>
                    {idx + 1}. {t.texto}
                  </div>
                  {t.total === 0 ? (
                    <span style={{ color: '#999' }}>Sin respuestas todavía</span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {t.orderedKeys.map((opt) => {
                        const n = t.counts[opt];
                        const pct = Math.round((n / t.total) * 100);
                        const color = answerColor(opt);
                        return (
                          <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ minWidth: 120, fontSize: 14, color: '#333', fontWeight: 500 }}>
                              {opt}
                            </div>
                            <div style={{ flex: 1, height: 24, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                              <div
                                style={{
                                  width: `${pct}%`,
                                  height: '100%',
                                  background: color,
                                  transition: 'width 400ms ease-out',
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                paddingRight: 10,
                                fontSize: 13, fontWeight: 600,
                                color: pct > 50 ? '#fff' : '#444',
                                mixBlendMode: pct > 50 ? 'normal' : 'normal',
                              }}>
                                {n} · {pct}%
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </Card>
          )}

          {/* Detailed table */}
          <Card style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 0', color: '#888', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Respuestas individuales
            </div>
            <Table
              rowKey="id"
              dataSource={data ? data.data : []}
              columns={columns}
              loading={loading}
              pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
              scroll={{ x: 900 }}
              style={{ marginTop: 12 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Sin respuestas todavía"
                    style={{ padding: 24 }}
                  />
                ),
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EncuestaAdmin;
